// src/components/claims/ClaimsPanel.jsx
//
// «Affermazioni nel testo» — the claims the FINAL text makes and what backs each one
// (BE ≥ e162398: results.verification). Shared by the generator run card
// (Step3History) and the run detail page; the sources are SourcesPanel, beside it.
// - verification null/absent (old runs, pinterest, youtube) → renders nothing; ⛔ no
//   legacy renderer from the removed reviews/sources.
// - Starts OPEN when there is something to act on (PARTIAL / CONTRADICTED / NOT_FOUND
//   row or a critical warning — claimsModel.needsAttention), else closed.
// - Rows come in catalogue order (all SECTION rows before SUBSECTION rows), grouped
//   by part + ordinal under their heading — not reading order. Consecutive groups of
//   the same part that would print the same heading (e.g. two headingless BODY rows)
//   merge into one. Each group is a bordered block: a band with the part's name as a tag
//   (when the label is a heading — so «Sottosezione» says what the order cannot), the
//   label and the group's claim count. `location` has no parent, so a subsection cannot
//   be nested under its section.
// - `not_in_final_text` counts the WHOLE run (claims checked in a draft that the style
//   pass removed; they are not listed): printed after the groups, never above the first.
// - The header names the outcome too when the figures were not checked (or the list
//   is unavailable), so a closed panel never reads as a clean result.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Shield } from 'lucide-react';
import CollapsiblePanel from './CollapsiblePanel.jsx';
import GroupBlock from './GroupBlock.jsx';
import StatusChip from './StatusChip.jsx';
import ClaimRow from './ClaimRow.jsx';
import { locationLabel } from './locationLabel.js';
import { claimsOf, groupClaims, needsAttention, verificationOf } from './claimsModel.js';
import { COLORS, RADII } from '../../constants/theme.js';

const COUNT_ORDER = ['CONTRADICTED', 'PARTIAL', 'NOT_FOUND', 'SUPPORTED', 'FROM_BRAND', 'FROM_BRIEF', 'NOT_CHECKED'];
const OUTCOME_LINES = new Set(['UNREVIEWED_SKIPPED', 'UNREVIEWED_ERROR', 'BUILD_ERROR']);

// Pure: merges consecutive groups of the same part whose displayed label is identical.
export function mergeSameLabel(groups, labelOf) {
  return groups.reduce((merged, group) => {
    const label = labelOf(group);
    const last = merged[merged.length - 1];
    if (last && last.label === label && last.part === group.part) {
      return [...merged.slice(0, -1), { ...last, claims: [...last.claims, ...group.claims] }];
    }
    return [...merged, {
      key: group.key, label, part: group.part, hasHeading: Boolean(group.heading?.trim?.()), claims: group.claims,
    }];
  }, []);
}

export default function ClaimsPanel({ results, t = {}, w = (o) => `rgba(0,0,0,${o})` }) {
  const { t: tr } = useTranslation('outputs');
  const verification = verificationOf(results);
  if (!verification) return null;

  const claims = claimsOf(verification);
  const counts = verification.counts || {};
  const notInFinal = Number.isInteger(counts.not_in_final_text) ? counts.not_in_final_text : 0;
  const muted = t.textMuted || COLORS.textSecondary;
  const chips = (
    <>
      {OUTCOME_LINES.has(verification.outcome) && (
        <span style={{ fontSize: '11px', color: muted, padding: '1px 8px', borderRadius: RADII.pill, border: `1px solid ${w(0.12)}` }}>
          {tr(`claims.outcomeShort.${verification.outcome}`)}
        </span>
      )}
      {COUNT_ORDER.filter((s) => counts[s] > 0).map((s) => <StatusChip key={s} status={s} count={counts[s]} />)}
    </>
  );

  return (
    <CollapsiblePanel
      icon={Shield}
      title={tr('claims.title')}
      description={tr('claims.description')}
      chips={chips}
      initiallyOpen={needsAttention(verification, results?.warnings)}
      bodyId={`claims-panel-${results?._taskId || 'run'}`}
      t={t}
      w={w}
    >
      {OUTCOME_LINES.has(verification.outcome) && (
        <p style={{ margin: '0 0 8px', fontSize: '12.5px', color: t.text || COLORS.ink }}>{tr(`claims.outcome.${verification.outcome}`)}</p>
      )}
      {claims.length === 0 && verification.outcome !== 'BUILD_ERROR' && (
        <p style={{ margin: '0 0 8px', fontSize: '12.5px', color: muted }}>{tr('claims.outcome.empty')}</p>
      )}
      {mergeSameLabel(groupClaims(claims), (g) => locationLabel(g, tr)).map((group) => (
        <GroupBlock
          key={group.key}
          tag={group.hasHeading && group.part ? tr(`claims.part.${group.part}`, { defaultValue: group.part }) : null}
          label={group.label}
          countText={tr('claims.groupCount', { count: group.claims.length })}
          t={t}
          w={w}
        >
          {group.claims.map((claim, i) => <ClaimRow key={claim.id ?? i} claim={claim} t={t} />)}
        </GroupBlock>
      ))}
      {notInFinal > 0 && (
        <p style={{ margin: '12px 0 0', fontSize: '12px', color: muted }}>{tr('claims.notInFinalText', { count: notInFinal })}</p>
      )}
    </CollapsiblePanel>
  );
}
